import { VStack } from "@chakra-ui/react";
import type { CpvNode } from "./cpvHierarchy";
import { CpvTreeNode } from "./CpvTreeNode";

type Props = {
  nodes: CpvNode[];
  selected: Set<string>;
  expandedIds: Set<string>;
  onToggleSelect: (code: string) => void;
  onToggleExpand: (code: string) => void;
};

export const CpvCategoryTree = ({
  nodes,
  selected,
  expandedIds,
  onToggleSelect,
  onToggleExpand,
}: Props) => (
  <VStack align="stretch" spacing="0">
    {nodes.map((node) => (
      <CpvBranch
        key={node.code}
        node={node}
        selected={selected}
        expandedIds={expandedIds}
        onToggleSelect={onToggleSelect}
        onToggleExpand={onToggleExpand}
      />
    ))}
  </VStack>
);

const CpvBranch = ({
  node,
  selected,
  expandedIds,
  onToggleSelect,
  onToggleExpand,
}: {
  node: CpvNode;
  selected: Set<string>;
  expandedIds: Set<string>;
  onToggleSelect: (code: string) => void;
  onToggleExpand: (code: string) => void;
}) => {
  const isExpanded = expandedIds.has(node.code);
  const isSelected = selected.has(node.code);
  const hasSelectedDescendant = !isSelected && hasAnyDescendant(node, selected);
  const hasChildren = node.children.length > 0;

  return (
    <>
      <CpvTreeNode
        node={node}
        isSelected={isSelected}
        isExpanded={isExpanded}
        hasSelectedDescendant={hasSelectedDescendant}
        hasChildren={hasChildren}
        onToggleSelect={() => onToggleSelect(node.code)}
        onToggleExpand={() => onToggleExpand(node.code)}
      />
      {isExpanded &&
        node.children.map((child) => (
          <CpvBranch
            key={child.code}
            node={child}
            selected={selected}
            expandedIds={expandedIds}
            onToggleSelect={onToggleSelect}
            onToggleExpand={onToggleExpand}
          />
        ))}
    </>
  );
};

function hasAnyDescendant(node: CpvNode, selected: Set<string>): boolean {
  for (const child of node.children) {
    if (selected.has(child.code)) return true;
    if (hasAnyDescendant(child, selected)) return true;
  }
  return false;
}
